import { observer } from "mobx-react";
import * as React from "react";
import styled, { css } from "styled-components";

import Style from "@client/utils/Style";

const Wrapper = styled.div<{ secondary?: boolean }>`
  display: flex;
  flex-direction: column;
  ${props =>
    props.secondary
      ? css`
          &:after {
            content: " ";
            height: 4px;
            background: ${Style.palette.grayscale[1]};
            border-bottom-left-radius: ${Style.borderRadius};
            border-bottom-right-radius: ${Style.borderRadius};
          }
        `
      : ""}
`;
type HeaderProps = {
  secondary?: boolean;
  customStyle?: any;
};
const Header = styled.div<HeaderProps>`
  width: calc(100% - 16px);
  display: flex;
  border-bottom: 1px solid
    ${props =>
      props.secondary
        ? Style.palette.grayscale[5]
        : Style.palette.grayscale[2]};
  ${props =>
    props.secondary
      ? css`
          padding: 8px;
          background: ${Style.palette.grayscale[1]};
          border-top-left-radius: ${Style.borderRadius};
          border-top-right-radius: ${Style.borderRadius};
        `
      : css`
          padding-bottom: 8px;
        `}
  ${props => props.customStyle}
`;
type TableDataProps = {
  width: React.CSSProperties["width"];
};
const TableData = (props: TableDataProps) => css`
  width: 100%;
  max-width: ${props.width};
  padding-right: 32px;
  &:last-child {
    padding-right: 0;
  }
`;
type HeaderDataProps = TableDataProps & {
  secondary?: boolean;
};
const HeaderData = styled.div<HeaderDataProps>`
  ${props => TableData(props)}
  color: ${props =>
    props.secondary ? Style.palette.grayscale[5] : Style.palette.grayscale[3]};
  font-size: 10px;
  font-weight: ${Style.fontWeights.medium};
  text-transform: uppercase;
  line-height: 16px;
  &:last-child {
    padding-right: 0;
  }
`;
const Body = styled.div<{ customStyle?: any }>`
  display: flex;
  flex-direction: column;
  max-height: 238px;
  overflow: auto;
  ${props => props.customStyle}
`;
const Row = styled.div<{ secondary?: boolean }>`
  display: flex;
  padding: ${props => (props.secondary ? "8px" : "8px 0")};
  ${props =>
    props.secondary ? `background: ${Style.palette.grayscale[1]};` : ""}
  border-bottom: 1px solid ${props =>
    props.secondary ? "white" : Style.palette.grayscale[2]};
  flex-shrink: 0;
  ${props =>
    props.secondary
      ? css`
          &:last-child {
            border-bottom: 0;
            padding-bottom: 4px;
          }
        `
      : ""}
`;
type RowDataProps = TableDataProps & {
  customStyle?: any;
};
const RowData = styled.div<RowDataProps>`
  ${props => TableData(props)}
  color: ${Style.palette.grayscale[4]};
  ${props => props.customStyle};
  overflow: hidden;
  text-overflow: ellipsis;
`;
const NoItems = styled.div<{ secondary?: boolean }>`
  text-align: center;
  padding: 16px;
  ${props =>
    props.secondary ? `background: ${Style.palette.grayscale[1]};` : ""}
`;

type Props = {
  header: React.ReactNode[];
  items: Array<Array<React.ReactNode>>;
  columnsStyle?: any[];
  columnsWidths?: string[];
  className?: string;
  useSecondaryStyle?: boolean;
  noItemsLabel?: string;
  noItemsComponent?: React.ReactNode;
  noItemsStyle?: any;
  bodyStyle?: any;
  headerStyle?: any;
  style?: any;
};
@observer
class Table extends React.Component<Props> {
  static defaultProps: Partial<Props> = {
    columnsWidths: [],
    noItemsLabel: "No items!",
  };

  renderHeader() {
    const dataWidth = `${100 / this.props.header.length}%`;
    return (
      <Header
        secondary={this.props.useSecondaryStyle}
        customStyle={this.props.headerStyle}
      >
        {this.props.header.map((headerItem, i) => {
          return (
            <HeaderData
              width={
                this.props.columnsWidths && this.props.columnsWidths.length > 0
                  ? this.props.columnsWidths[i]
                  : dataWidth
              }
              key={i}
              secondary={this.props.useSecondaryStyle}
            >
              {headerItem}
            </HeaderData>
          );
        })}
      </Header>
    );
  }

  renderNoItems() {
    if (this.props.items.length > 0) {
      return null;
    }

    return (
      <NoItems
        secondary={this.props.useSecondaryStyle}
        style={this.props.noItemsStyle}
      >
        {this.props.noItemsComponent || this.props.noItemsLabel}
      </NoItems>
    );
  }

  renderItems() {
    if (this.props.items.length === 0) {
      return null;
    }

    const dataWidth = `${100 / this.props.header.length}%`;
    return (
      <Body customStyle={this.props.bodyStyle}>
        {this.props.items.map((row, i) => {
          return (
            <Row key={i} secondary={this.props.useSecondaryStyle}>
              {row.constructor === Array
                ? row.map((data, j) => {
                    let columnStyle = "";

                    if (this.props.columnsStyle) {
                      columnStyle = this.props.columnsStyle[j] || "";
                    }

                    return (
                      <RowData
                        customStyle={columnStyle}
                        width={dataWidth}
                        key={`${i}-${j}`}
                      >
                        {data}
                      </RowData>
                    );
                  })
                : row}
            </Row>
          );
        })}
      </Body>
    );
  }

  render() {
    return (
      <Wrapper
        className={this.props.className}
        secondary={this.props.useSecondaryStyle}
        style={this.props.style}
      >
        {this.renderHeader()}
        {this.renderItems()}
        {this.renderNoItems()}
      </Wrapper>
    );
  }
}

export default Table;
